import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router";
import { Dialog } from "../components/Dialog.tsx";
import { IconClose } from "../components/Icons.tsx";
import { useMedia } from "../lib/hooks.ts";
import { useMe, useSession } from "../lib/session.tsx";
import type { Permission } from "../api/types.ts";
import { fmt, useT, type Dict } from "../i18n/index.tsx";
import "./tour.css";

type StepKey = keyof Dict["tour"]["steps"];
/** `target`: CSS selector of what the step is about; it gets a ring and the rest of the page is dimmed. */
type Step = { key: StepKey; to: string; perms: Permission[]; target: string };

// Candidate steps per kind of user, in order; the first four the user can open are shown.
const STEPS: Record<StepKey, Step> = {
  networks: { key: "networks", to: "/vault", perms: ["vault:view"], target: "section.networks" },
  invite: { key: "invite", to: "/team", perms: ["team:invite"], target: '[data-tour="invite"]' },
  telegram: { key: "telegram", to: "/settings/security", perms: [], target: 'section[aria-labelledby="tg-h"]' },
  developers: { key: "developers", to: "/developers", perms: ["api_keys:view", "webhook:view"], target: 'section[aria-labelledby="keys-h"]' },
  overview: { key: "overview", to: "/", perms: ["overview:view"], target: "section.summary" },
  deposits: { key: "deposits", to: "/deposits", perms: ["deposits:view"], target: ".lookup, .lookup-icon" },
  approve: { key: "approve", to: "/payouts?status=pending_approval", perms: ["payouts:approve"], target: '.tabs [role="tab"]:nth-of-type(2)' },
  payouts: { key: "payouts", to: "/payouts", perms: ["payouts:view"], target: "main .table-wrap" },
  statement: { key: "statement", to: "/statement", perms: ["statement:view"], target: "main .table-wrap" },
  security: { key: "security", to: "/settings/security", perms: [], target: 'section[aria-labelledby="sess-h"]' },
};
const OWNER: StepKey[] = ["networks", "invite", "telegram", "developers", "overview", "security"];
const DEVELOPER: StepKey[] = ["developers", "overview", "deposits", "payouts", "security"];
const OTHERS: StepKey[] = ["overview", "deposits", "approve", "payouts", "statement", "security"];

/** Saved per user in this browser: null = never seen (show the welcome popup). */
type Saved = { step: number; done: boolean };
const storeKey = (userId: string) => `icn_tour_${userId}`;
function load(userId: string): Saved | null {
  try {
    const v = JSON.parse(localStorage.getItem(storeKey(userId)) ?? "null") as Saved | null;
    return v && typeof v.step === "number" ? v : null;
  } catch {
    return null;
  }
}
type Box = { top: number; left: number; width: number; height: number };
const PAD = 8;

/**
 * Where the step's target is on screen (null until it renders), clipped to the viewport. Polls each frame while a
 * step is active, since pages load their data and move things around; scrolls the target into view once.
 */
function useTargetBox(selector: string | null): Box | null {
  const [box, setBox] = useState<Box | null>(null);
  useEffect(() => {
    setBox(null);
    if (!selector) return;
    let raf = 0;
    let scrolled = false;
    let last = "";
    const tick = () => {
      const el = document.querySelector<HTMLElement>(selector);
      const r = el?.getBoundingClientRect();
      if (el && r && r.width > 0) {
        if (!scrolled) {
          scrolled = true;
          if (r.top < 70 || r.bottom > window.innerHeight - 16) el.scrollIntoView({ block: r.height > window.innerHeight * 0.6 ? "start" : "center", behavior: "smooth" });
        }
        const top = Math.max(r.top - PAD, 4);
        const bottom = Math.min(r.bottom + PAD, window.innerHeight - 4);
        const next = { top, left: Math.max(r.left - PAD, 4), width: Math.min(r.width + PAD * 2, window.innerWidth - 8), height: Math.max(bottom - top, 0) };
        const key = Object.values(next).map(Math.round).join(",");
        if (key !== last) {
          last = key;
          setBox(next);
        }
      } else if (last) {
        last = "";
        setBox(null);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [selector]);
  return box;
}

/** Card next to the highlighted box: below it, else above it, else beside it, else the bottom corner. */
function cardStyle(box: Box | null, card: { w: number; h: number }): CSSProperties | undefined {
  if (!box || !card.h) return undefined;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 14;
  const left = Math.min(Math.max(box.left, 16), vw - card.w - 16);
  if (box.top + box.height + gap + card.h <= vh - 16) return { top: box.top + box.height + gap, left, bottom: "auto", insetInlineEnd: "auto" };
  if (box.top - gap - card.h >= 16) return { top: box.top - gap - card.h, left, bottom: "auto", insetInlineEnd: "auto" };
  const top = Math.min(Math.max(box.top, 16), vh - card.h - 16);
  if (box.left + box.width + gap + card.w <= vw - 16) return { top, left: box.left + box.width + gap, bottom: "auto", insetInlineEnd: "auto" };
  if (box.left - gap - card.w >= 16) return { top, left: box.left - gap - card.w, bottom: "auto", insetInlineEnd: "auto" };
  return undefined;
}

function save(userId: string, v: Saved) {
  try {
    localStorage.setItem(storeKey(userId), JSON.stringify(v));
  } catch {
    /* storage unavailable: the guide simply shows again next time */
  }
}

/**
 * Getting-started guide. First login: a welcome popup listing four steps. "Start" opens step 1's page and keeps a small
 * card in the corner with what to do there; Next moves to the next page. `kick` (from the user menu) opens it again.
 */
export function Tour({ kick }: { kick: number }) {
  const t = useT();
  const me = useMe();
  const { canAny } = useSession();
  const navigate = useNavigate();
  const loc = useLocation();
  const userId = me.user.id;

  const order = me.role.key === "owner" ? OWNER : me.role.key === "developer" ? DEVELOPER : OTHERS;
  const steps = order.map((k) => STEPS[k]).filter((s) => !s.perms.length || canAny(...s.perms));
  // "approve" and "payouts" are the same page: keep only the first one the user gets.
  const shown = steps.filter((s, i) => !(s.key === "payouts" && steps.slice(0, i).some((x) => x.key === "approve"))).slice(0, 4);

  const [saved, setSaved] = useState<Saved | null>(() => load(userId));
  const [welcome, setWelcome] = useState(() => saved === null);

  useEffect(() => {
    if (kick > 0) setWelcome(true);
  }, [kick]);

  const update = (v: Saved) => {
    save(userId, v);
    setSaved(v);
  };
  const go = (i: number) => {
    update({ step: i, done: false });
    const s = shown[i];
    if (s) navigate(s.to);
  };
  const finish = () => update({ step: saved?.step ?? 0, done: true });

  const total = shown.length;
  const active = saved && !saved.done && !welcome && total ? Math.min(saved.step, total - 1) : null;
  const step = (active !== null && shown[active]) || null;
  const onPage = step ? loc.pathname === step.to.split("?")[0] : false;

  const phone = useMedia("(max-width: 960px)");
  const cardRef = useRef<HTMLElement>(null);
  const [card, setCard] = useState({ w: 0, h: 0 });
  const box = useTargetBox(step && onPage ? step.target : null);
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setCard({ w: el.offsetWidth, h: el.offsetHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [step?.key]);
  const placed = phone ? undefined : cardStyle(box, card);

  if (!total) return null;

  return (
    <>
      <Dialog
        open={welcome}
        onClose={() => {
          setWelcome(false);
          if (!saved) update({ step: 0, done: true });
        }}
        title={fmt(t.tour.welcomeTitle, { name: me.user.name })}
        description={t.tour.welcomeBody}
        size="md"
      >
        <ol className="tour-list">
          {shown.map((s, i) => (
            <li key={s.key}>
              <span className="tour-num" aria-hidden="true">{i + 1}</span>
              <div>
                <strong>{t.tour.steps[s.key].title}</strong>
              </div>
            </li>
          ))}
        </ol>
        <p className="tour-hint">{t.tour.reopenHint}</p>
        <div className="dlg-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setWelcome(false);
              if (!saved) update({ step: 0, done: true });
            }}
          >
            {t.tour.later}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-autofocus
            onClick={() => {
              setWelcome(false);
              go(0);
            }}
          >
            {t.tour.start}
          </button>
        </div>
      </Dialog>

      {step && active !== null && box && <div className="tour-spot" style={box} aria-hidden="true" />}
      {step && active !== null && (
        <section ref={cardRef} className="tour-card" style={placed} aria-label={t.tour.label}>
          <div className="tour-card-head">
            <span className="tour-step">{fmt(t.tour.stepOf, { n: active + 1, total })}</span>
            <button type="button" className="icon-btn" onClick={finish} aria-label={t.tour.close} title={t.tour.close}>
              <IconClose size={18} />
            </button>
          </div>
          <div className="tour-dots" aria-hidden="true">
            {shown.map((s, i) => <span key={s.key} className={i <= active ? "on" : ""} />)}
          </div>
          <h2>{t.tour.steps[step.key].title}</h2>
          <p>{t.tour.steps[step.key].body}</p>
          <div className="btn-row tour-actions">
            {!onPage && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(step.to)}>
                {t.tour.goThere}
              </button>
            )}
            <span className="tour-grow" />
            {active > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => go(active - 1)}>
                {t.tour.back}
              </button>
            )}
            {active < total - 1 ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => go(active + 1)}>
                {t.tour.next}
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" onClick={finish}>
                {t.tour.finish}
              </button>
            )}
          </div>
        </section>
      )}
    </>
  );
}
